import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Clock,
  FileText,
  Image as ImageIcon,
  Loader2,
  MapPin,
  Package,
  Phone,
  Truck,
  User,
  X,
} from 'lucide-react';

function getStatusPresentation(status) {
  const normalized = String(status || '').toLowerCase();
  const presentations = {
    pending: { label: 'Pendiente', className: 'bg-amber-50 text-amber-700 ring-amber-200' },
    encamino: { label: 'En camino', className: 'bg-blue-50 text-blue-700 ring-blue-200' },
    entregado: { label: 'Entregado', className: 'bg-green-50 text-green-700 ring-green-200' },
    rechazado: { label: 'Rechazado', className: 'bg-red-50 text-red-700 ring-red-200' },
    cancelado: { label: 'Cancelado', className: 'bg-slate-100 text-slate-600 ring-slate-200' },
  };

  return presentations[normalized] || {
    label: status || 'Sin estado',
    className: 'bg-slate-100 text-slate-600 ring-slate-200',
  };
}

function formatDeliveryDate(value) {
  if (!value) return 'No registrada';

  const datePart = String(value).slice(0, 10);
  const [year, month, day] = datePart.split('-').map(Number);

  if (!year || !month || !day) return 'No registrada';

  return new Intl.DateTimeFormat('es-PE', {
    timeZone: 'America/Lima',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date(Date.UTC(year, month - 1, day, 12)));
}

function formatMoney(value, emptyLabel = 'No registrado') {
  if (value === null || value === undefined || value === '') return emptyLabel;

  const amount = Number(value);
  if (!Number.isFinite(amount)) return emptyLabel;

  return new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: 'PEN',
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatDateTime(value) {
  if (!value) return 'No registrada';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No registrada';

  return new Intl.DateTimeFormat('es-PE', {
    timeZone: 'America/Lima',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function DetailCard({ icon: Icon, label, value, children, wide = false, accent = false }) {
  return (
    <div
      className={`rounded-xl border p-4 ${wide ? 'sm:col-span-2' : ''} ${
        accent
          ? 'border-[#67A43C]/20 bg-gradient-to-br from-[#67A43C]/5 to-[#E6B43C]/5'
          : 'border-slate-200 bg-slate-50'
      }`}
    >
      <div className="mb-2 flex items-center gap-2 text-slate-500">
        <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span className="text-[11px] font-bold uppercase tracking-wide font-montserrat">
          {label}
        </span>
      </div>
      {children || (
        <p className="break-words text-sm font-semibold text-slate-900 font-montserrat">
          {value || 'No registrado'}
        </p>
      )}
    </div>
  );
}

function DeliveryImageCarousel({ images, deliveryId }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setCurrentIndex(0);
    setImageFailed(false);
  }, [deliveryId, images]);

  if (images.length === 0) {
    return (
      <div className="flex min-h-40 flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
        <ImageIcon className="h-9 w-9 text-slate-300" aria-hidden="true" />
        <p className="mt-3 text-sm font-semibold text-slate-600 font-montserrat">
          Esta entrega no tiene imágenes
        </p>
      </div>
    );
  }

  function showPrevious() {
    setCurrentIndex((current) => (current - 1 + images.length) % images.length);
    setImageFailed(false);
  }

  function showNext() {
    setCurrentIndex((current) => (current + 1) % images.length);
    setImageFailed(false);
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-950">
      <div className="relative flex aspect-video max-h-[420px] items-center justify-center">
        {imageFailed ? (
          <div className="flex flex-col items-center justify-center px-6 text-center text-white">
            <AlertCircle className="h-8 w-8 text-white/60" aria-hidden="true" />
            <p className="mt-3 text-sm font-semibold font-montserrat">
              No pudimos cargar esta imagen
            </p>
          </div>
        ) : (
          <img
            src={images[currentIndex]}
            alt={`Evidencia de la entrega #${deliveryId}, imagen ${currentIndex + 1}`}
            className="h-full w-full object-contain"
            onError={() => setImageFailed(true)}
          />
        )}

        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={showPrevious}
              className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-800 shadow-lg transition hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E6B43C]"
              aria-label="Ver imagen anterior"
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={showNext}
              className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-800 shadow-lg transition hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E6B43C]"
              aria-label="Ver imagen siguiente"
            >
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </>
        )}

        <span aria-live="polite" className="absolute bottom-3 right-3 rounded-full bg-slate-950/75 px-3 py-1 text-xs font-bold text-white backdrop-blur font-montserrat">
          {currentIndex + 1} / {images.length}
        </span>
      </div>

      {images.length > 1 && (
        <div className="flex items-center justify-center gap-1 overflow-x-auto border-t border-white/10 px-3 py-1">
          {images.map((image, index) => (
            <button
              // The URL can repeat, so the position is part of the stable carousel key.
              key={`${image}-${index}`}
              type="button"
              onClick={() => {
                setCurrentIndex(index);
                setImageFailed(false);
              }}
              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E6B43C]"
              aria-label={`Ver imagen ${index + 1}`}
              aria-current={index === currentIndex ? 'true' : undefined}
            >
              <span
                className={`h-2.5 w-2.5 rounded-full transition ${
                  index === currentIndex ? 'bg-[#E6B43C]' : 'bg-white/35 hover:bg-white/60'
                }`}
                aria-hidden="true"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DeliveryDetailsModal({ isOpen, delivery, isLoading, error, onBack, onClose }) {
  const images = useMemo(
    () => {
      const rawImages = Array.isArray(delivery?.image_urls)
        ? delivery.image_urls
        : Array.isArray(delivery?.images)
        ? delivery.images
        : [];

      return Array.from(
        new Set(
          rawImages.filter(
            (imageUrl) => typeof imageUrl === 'string' && imageUrl.trim()
          )
        )
      );
    },
    [delivery]
  );
  const dialogRef = useRef(null);
  const initialFocusRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimeout = window.setTimeout(() => initialFocusRef.current?.focus(), 0);

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusableElements = dialogRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimeout);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const status = getStatusPresentation(delivery?.status);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      role="presentation"
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delivery-details-title"
        className="flex max-h-[92vh] max-h-[92dvh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 border-b border-slate-200 bg-gradient-to-r from-[#B1336E]/5 to-[#E6B43C]/5 px-4 py-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <button
              ref={initialFocusRef}
              type="button"
              onClick={onBack}
              className="flex-shrink-0 rounded-lg p-2 text-slate-500 transition-colors hover:bg-white hover:text-[#B1336E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B1336E]"
              aria-label="Volver a los resultados"
            >
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            <div className="min-w-0">
              <h2
                id="delivery-details-title"
                className="truncate text-lg font-extrabold text-slate-900 font-montserrat sm:text-xl"
              >
                Detalle de entrega
              </h2>
              <p className="truncate text-xs text-slate-500 font-montserrat sm:text-sm">
                {delivery ? `Entrega #${delivery.id}` : 'Cargando información...'}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 rounded-lg p-2 text-slate-500 transition-colors hover:bg-white hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B1336E]"
            aria-label="Cerrar detalle"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
          {isLoading ? (
            <div className="flex min-h-72 flex-col items-center justify-center text-slate-500">
              <Loader2 className="h-8 w-8 animate-spin text-[#B1336E]" aria-hidden="true" />
              <p className="mt-4 text-sm font-semibold font-montserrat">
                Cargando detalle de la entrega...
              </p>
            </div>
          ) : error ? (
            <div className="flex min-h-72 flex-col items-center justify-center text-center">
              <AlertCircle className="h-10 w-10 text-red-500" aria-hidden="true" />
              <p className="mt-4 max-w-md text-sm font-semibold text-red-700 font-montserrat">
                {error}
              </p>
              <button
                type="button"
                onClick={onBack}
                className="mt-5 inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B1336E] font-montserrat"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                Volver a buscar
              </button>
            </div>
          ) : delivery ? (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400 font-montserrat">
                    Entrega
                  </p>
                  <p className="mt-1 text-2xl font-extrabold text-[#B1336E] font-montserrat">
                    #{delivery.id}
                  </p>
                </div>
                <span
                  className={`rounded-full px-4 py-2 text-sm font-bold ring-1 ring-inset font-montserrat ${status.className}`}
                >
                  {status.label}
                </span>
              </div>

              <section aria-labelledby="delivery-images-title">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h3
                    id="delivery-images-title"
                    className="flex items-center gap-2 text-sm font-extrabold text-slate-800 font-montserrat"
                  >
                    <ImageIcon className="h-5 w-5 text-[#B1336E]" aria-hidden="true" />
                    Imágenes de la entrega
                  </h3>
                  {images.length > 0 && (
                    <span className="text-xs text-slate-500 font-montserrat">
                      {images.length} {images.length === 1 ? 'imagen' : 'imágenes'}
                    </span>
                  )}
                </div>
                <DeliveryImageCarousel images={images} deliveryId={delivery.id} />
              </section>

              <section aria-labelledby="delivery-information-title">
                <h3
                  id="delivery-information-title"
                  className="mb-3 text-sm font-extrabold text-slate-800 font-montserrat"
                >
                  Información de la entrega
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <DetailCard icon={User} label="Destinatario" value={delivery.recipient_name} />
                  <DetailCard icon={Phone} label="Teléfono">
                    {delivery.recipient_phone ? (
                      <a
                        href={`tel:${delivery.recipient_phone}`}
                        className="break-words text-sm font-semibold text-[#B1336E] hover:underline font-montserrat"
                      >
                        {delivery.recipient_phone}
                      </a>
                    ) : (
                      <p className="text-sm font-semibold text-slate-900 font-montserrat">
                        No registrado
                      </p>
                    )}
                  </DetailCard>
                  <DetailCard
                    icon={CalendarDays}
                    label="Fecha de entrega"
                    value={formatDeliveryDate(delivery.delivery_date)}
                  />
                  {delivery.arrival_time && (
                    <DetailCard
                      icon={Clock}
                      label="Hora de llegada"
                      value={formatDateTime(delivery.arrival_time)}
                    />
                  )}
                  <DetailCard
                    icon={MapPin}
                    label="Distrito"
                    value={delivery.district || delivery.route_zone_name}
                  />
                  <DetailCard icon={MapPin} label="Dirección" value={delivery.address} wide />
                  <DetailCard icon={Package} label="Producto" value={delivery.product} wide />
                  {(delivery.tracking_code || delivery.product_sku || delivery.package_status) && (
                    <DetailCard icon={Package} label="Paquete" wide>
                      <div className="grid gap-2 text-sm text-slate-800 sm:grid-cols-3">
                        <p className="font-montserrat">
                          <span className="block text-[11px] font-bold uppercase text-slate-400">
                            Tracking
                          </span>
                          <span className="font-semibold">{delivery.tracking_code || '—'}</span>
                        </p>
                        <p className="font-montserrat">
                          <span className="block text-[11px] font-bold uppercase text-slate-400">
                            SKU
                          </span>
                          <span className="font-semibold">{delivery.product_sku || '—'}</span>
                        </p>
                        <p className="font-montserrat">
                          <span className="block text-[11px] font-bold uppercase text-slate-400">
                            Estado
                          </span>
                          <span className="font-semibold">{delivery.package_status || '—'}</span>
                        </p>
                      </div>
                    </DetailCard>
                  )}
                </div>
              </section>

              <section aria-labelledby="delivery-financial-title">
                <h3
                  id="delivery-financial-title"
                  className="mb-3 text-sm font-extrabold text-slate-800 font-montserrat"
                >
                  Información financiera
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <DetailCard
                    icon={CircleDollarSign}
                    label="Monto a cobrar"
                    value={formatMoney(delivery.charge)}
                    accent
                  />
                  <DetailCard
                    icon={CircleDollarSign}
                    label="Monto cobrado"
                    value={formatMoney(delivery.charged, 'Pendiente')}
                    accent
                  />
                </div>
              </section>

              {(delivery.driver || delivery.notes || delivery.notes_driver) && (
                <section aria-labelledby="delivery-operation-title">
                  <h3
                    id="delivery-operation-title"
                    className="mb-3 text-sm font-extrabold text-slate-800 font-montserrat"
                  >
                    Información operativa
                  </h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {delivery.driver && (
                      <DetailCard icon={Truck} label="Conductor" wide>
                        <p className="text-sm font-semibold text-slate-900 font-montserrat">
                          {delivery.driver.username || 'No registrado'}
                        </p>
                        {delivery.driver.phone_number && (
                          <a
                            href={`tel:${delivery.driver.phone_number}`}
                            className="mt-1 inline-block text-xs font-semibold text-[#B1336E] hover:underline font-montserrat"
                          >
                            {delivery.driver.phone_number}
                          </a>
                        )}
                      </DetailCard>
                    )}
                    {delivery.notes && (
                      <DetailCard icon={FileText} label="Notas de la tienda" value={delivery.notes} wide />
                    )}
                    {delivery.notes_driver && (
                      <DetailCard
                        icon={FileText}
                        label="Notas del conductor"
                        value={delivery.notes_driver}
                        wide
                      />
                    )}
                  </div>
                </section>
              )}
            </div>
          ) : null}
        </div>

        <footer className="flex justify-end border-t border-slate-200 bg-white px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-gradient-to-r from-[#B1336E] to-[#E6B43C] px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B1336E] focus-visible:ring-offset-2 font-montserrat"
          >
            Cerrar
          </button>
        </footer>
      </section>
    </div>
  );
}

export default DeliveryDetailsModal;
